/**
 * Minimal element builder. The widget deliberately ships no UI framework — it is
 * injected into pages we also own, and every kilobyte here is a kilobyte added to
 * those pages' load.
 */

type Attrs = Record<string, string | number | boolean | undefined>;
type Child = Node | string | null | undefined | false;

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);

  for (const [key, value] of Object.entries(attrs)) {
    if (value === undefined || value === false) continue;
    if (key === 'class') {
      node.className = String(value);
    } else if (key === 'text') {
      node.textContent = String(value);
    } else {
      node.setAttribute(key, value === true ? '' : String(value));
    }
  }

  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    node.append(typeof child === 'string' ? document.createTextNode(child) : child);
  }

  return node;
}

/** Namespaced SVG builder, needed for the icons. */
export function svg(path: string, size = 16): SVGSVGElement {
  const ns = 'http://www.w3.org/2000/svg';
  const root = document.createElementNS(ns, 'svg');
  root.setAttribute('viewBox', '0 0 24 24');
  root.setAttribute('width', String(size));
  root.setAttribute('height', String(size));
  root.setAttribute('fill', 'none');
  root.setAttribute('stroke', 'currentColor');
  root.setAttribute('stroke-width', '2');
  root.setAttribute('stroke-linecap', 'round');
  root.setAttribute('stroke-linejoin', 'round');
  root.setAttribute('aria-hidden', 'true');

  const node = document.createElementNS(ns, 'path');
  node.setAttribute('d', path);
  root.append(node);
  return root;
}

export const icons = {
  bug: 'M8 2l1.5 1.5M16 2l-1.5 1.5M12 20v-9M6 13H2m20 0h-4M5 8a7 7 0 0114 0v4a7 7 0 01-14 0z',
  close: 'M18 6L6 18M6 6l12 12',
  check: 'M20 6L9 17l-5-5',
  undo: 'M9 14L4 9l5-5M4 9h11a5 5 0 010 10h-3',
  rect: 'M3 5h18v14H3z',
  arrow: 'M5 19L19 5M12 5h7v7',
  pen: 'M12 19l7-7 3 3-7 7-3-3zM18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5zM2 2l7.586 7.586',
  blackout: 'M3 6h18v12H3z',
} as const;
